import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { FaCopy, FaKey, FaSync } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import config from '../config';

const server_URL = config.SERVER_URL;

const CustomerApi = () => {
  const [apiKey, setApiKey] = useState(null);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [isSubuser, setIsSubuser] = useState(false);
  const [subuserModules, setSubuserModules] = useState([]);
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    setIsSubuser(userData.is_subuser || false);
    
    // Load subuser modules if it's a subuser
    if (userData.is_subuser) {
      const modules = userData.modules || JSON.parse(localStorage.getItem('subuser_modules') || '[]');
      setSubuserModules(Array.isArray(modules) ? modules : []);
    }
    
    // Check if user can access this page
    const canAccess = !userData.is_subuser || (userData.is_subuser && (userData.modules || JSON.parse(localStorage.getItem('subuser_modules') || '[]')).includes('customer-api'));
    
    // Only fetch API key if user can access (regular user or subuser with customer-api module)
    if (canAccess) {
      fetchApiKey();
      fetchEvents();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchApiKey = async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customer_api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get',
          user_id: userData.id,
          user_role: userData.role_id,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        setApiKey(data.data);
      } else {
        showToast(data.error || 'Failed to fetch API key', 'error');
      }
    } catch (error) {
      console.error('Error fetching API key:', error);
      showToast('Error fetching API key', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    setIsCreating(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customer_api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'create',
          user_id: userData.id,
          user_role: userData.role_id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        setApiKey(data.data);
        showToast(data.message || 'API key created successfully', 'success');
      } else {
        showToast(data.error || 'Failed to create API key', 'error');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      showToast('Error creating API key', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyApiKey = () => {
    if (apiKey && apiKey.api_key) {
      navigator.clipboard.writeText(apiKey.api_key);
      showToast('API key copied to clipboard', 'success');
    }
  };

  const handleToggleEnable = async () => {
    setIsToggling(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customer_api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'toggle_enable',
          user_id: userData.id,
          user_role: userData.role_id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        setApiKey(data.data);
        showToast(data.message || 'API key status updated successfully', 'success');
      } else {
        showToast(data.error || 'Failed to update API key status', 'error');
      }
    } catch (error) {
      console.error('Error toggling API key:', error);
      showToast('Error updating API key status', 'error');
    } finally {
      setIsToggling(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Are you sure you want to regenerate your API key? The old key will no longer work.')) {
      return;
    }

    setIsRegenerating(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customer_api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'regenerate',
          user_id: userData.id,
          user_role: userData.role_id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        setApiKey(data.data);
        showToast(data.message || 'API key regenerated successfully', 'success');
      } else {
        showToast(data.error || 'Failed to regenerate API key', 'error');
      }
    } catch (error) {
      console.error('Error regenerating API key:', error);
      showToast('Error regenerating API key', 'error');
    } finally {
      setIsRegenerating(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_public_events',
          user_role: userData.role_id,
          user_id: userData.id, // Pass user_id to filter events
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setEvents(data.data);
      } else {
        console.error('Failed to fetch events:', data.error);
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const handleCopyEventKey = (eventKey) => {
    navigator.clipboard.writeText(eventKey);
    showToast(`Event key "${eventKey}" copied to clipboard`, 'success');
  };

  // Check if user can access this page
  // Regular users (from users table) can access
  // Subusers can access only if 'customer-api' module is enabled
  const canAccess = !isSubuser || (isSubuser && subuserModules.includes('customer-api'));
  
  if (!canAccess) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert severity="error">
          <Typography variant="h6">Unauthorized Access</Typography>
          <Typography variant="body1">
            You do not have permission to access this page.
          </Typography>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            <FaKey style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            API Keys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your API key for accessing the API endpoints.
          </Typography>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <Paper sx={{ p: 4 }}>
            {!apiKey ? (
              <Stack spacing={3} alignItems="center">
                <Typography variant="h6" color="text.secondary">
                  No API key found
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Click the button below to generate a new API key for your account.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleCreateApiKey}
                  disabled={isCreating}
                  startIcon={<FaKey />}
                  sx={{ mt: 2 }}
                >
                  {isCreating ? 'Creating...' : 'Create API Key'}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Typography variant="h6" gutterBottom>
                  Your API Key
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Keep your API key secure and do not share it publicly.
                </Typography>
                <TextField
                  fullWidth
                  label="API Key"
                  value={apiKey.api_key || ''}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleCopyApiKey}
                          edge="end"
                          title="Copy API Key"
                        >
                          <FaCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  }}
                />
                
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={apiKey.enabled === 1}
                        onChange={handleToggleEnable}
                        disabled={isToggling}
                        color="primary"
                      />
                    }
                    label={apiKey.enabled === 1 ? 'Enabled' : 'Disabled'}
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FaSync />}
                    onClick={handleRegenerate}
                    disabled={isRegenerating}
                    sx={{ ml: 'auto' }}
                  >
                    {isRegenerating ? 'Regenerating...' : 'Regenerate Key'}
                  </Button>
                </Stack>
                
                {apiKey.created_at && (
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(apiKey.created_at).toLocaleString()}
                  </Typography>
                )}

                {/* API Details Section */}
                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" gutterBottom>
                    API Details
                  </Typography>
                  
                  <Stack spacing={3} sx={{ mt: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Endpoint:
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          wordBreak: 'break-all',
                        }}
                      >
                        {server_URL}/api/events
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Parameters:
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="body2">
                            <strong>API_KEY</strong> (required) - Your API key
                          </Typography>
                          <Typography variant="body2">
                            <strong>EVENT</strong> (required) - Event key from Events list below
                          </Typography>
                          <Typography variant="body2">
                            <strong>email</strong> (required) - Email address
                          </Typography>
                          <Typography variant="body2">
                            <strong>first_name</strong> (required) - First name
                          </Typography>
                          <Typography variant="body2">
                            <strong>last_name</strong> (required) - Last name
                          </Typography>
                          <Typography variant="body2">
                            <strong>custom_fields</strong> (optional) - JSON object with key-value pairs
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        Available Events:
                      </Typography>
                      {events.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No events available
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Event Name</strong></TableCell>
                                <TableCell><strong>Event Key</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell><strong>Action</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {events.map((event, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>{event.name}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={event.key}
                                      size="small"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        bgcolor: '#e3f2fd',
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {event.description ? (
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {event.description}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                                        No description
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyEventKey(event.key)}
                                      title="Copy Event Key"
                                    >
                                      <FaCopy />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Use the Event Key value in the EVENT parameter of your API request
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Example Request:
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          overflowX: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`POST ${server_URL}/api/events

Parameters:
  API_KEY: ${apiKey.api_key}
  EVENT: EVENT_KEY_1
  email: user@example.com
  first_name: John
  last_name: Doe
  custom_fields: {
    "key1": "value1",
    "key2": "value2",
    "custom_field": "custom_value"
  }`}
                        </pre>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Example cURL:
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          overflowX: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`curl -X POST "${server_URL}/api/events" \\
  -H "Content-Type: application/json" \\
  -d '{
    "API_KEY": "${apiKey.api_key}",
    "EVENT": "EVENT_KEY_1",
    "email": "user@example.com",
    "first_name": "John",
    "last_name": "Doe",
    "custom_fields": {
      "key1": "value1",
      "key2": "value2",
      "custom_field": "custom_value"
    }
  }'`}
                        </pre>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Paper>
        )}
      </Stack>

      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToastState({ ...toastState, open: false })}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CustomerApi;

